import React from "react";
import { Box, Grid, HStack, Stack, Switch, Text } from "@chakra-ui/react";
import { DashboardDeleteButton, DashboardInput, DashboardTruncatedText } from "components/utils/FormControls";
import { FaBox } from "react-icons/fa";
import { HiCheck, HiX } from "react-icons/hi";
import { DataCard } from "components/utils/DataCard";
import { FormDialog } from "components/utils/FormDialog";
import { TagCombobox } from "components/utils/TagCombobox";
import { useDialogForm } from "utils/useDialogForm";
import { getAvatarHexColor, getAvatarTint } from "utils/avatar";
import { asArray } from "../shared/helpers";
import {
  useDashboardActividadMateriales,
  useDashboardMaterialesCatalog,
  useDeleteDashboardActividadMaterial,
  useUpsertDashboardActividadMaterial,
} from "features/dashboard/actividades/hooks";

function toIntOrNull(v) {
  const n = Number(v);
  return Number.isInteger(n) ? n : null;
}

function materialTone(seed) {
  const solid = getAvatarHexColor(seed);
  return {
    solid,
    bg: getAvatarTint(seed, 12),
  };
}

export const PrevioMaterialesCard = ({ actividad, actividadId, patch }) => {
  const dialog = useDialogForm({ materialesRequeridos: true, rows: [] });
  const [localError, setLocalError] = React.useState(null);

  const materialesQ = useDashboardActividadMateriales(actividadId, !!actividadId);
  const catalogQ = useDashboardMaterialesCatalog(true);

  const upsertMaterial = useUpsertDashboardActividadMaterial();
  const deleteMaterial = useDeleteDashboardActividadMaterial();

  const saving = upsertMaterial.isPending || deleteMaterial.isPending || patch?.isPending;
  const apiError =
    (upsertMaterial.isError && String(upsertMaterial.error?.message || upsertMaterial.error)) ||
    (deleteMaterial.isError && String(deleteMaterial.error?.message || deleteMaterial.error)) ||
    (patch?.isError && String(patch.error?.message || patch.error)) ||
    null;
  const errorText = localError || apiError;
  const materialesRequeridos = actividad?.materialesRequeridos !== false;

  const dbRows = asArray(materialesQ.data).map((row) => ({
    materialId: row.materialId,
    cantidad: Number(row.cantidad ?? 0),
  }));

  const catalogRows = asArray(catalogQ.data);
  const materialById = React.useMemo(() => {
    const map = new Map();
    for (const m of catalogRows) map.set(String(m.id), m);
    return map;
  }, [catalogRows]);

  const materialItems = React.useMemo(
    () =>
      catalogRows
        .map((m) => ({ value: String(m.id), label: m.nombre || "Material" }))
        .filter((m) => m.value),
    [catalogRows]
  );

  function openDialog() {
    setLocalError(null);
    dialog.openWith({ materialesRequeridos, rows: dbRows });
  }

  function setMaterialesRequeridos(value) {
    dialog.setField("materialesRequeridos", value);
    if (!value) dialog.setField("rows", []);
  }

  function setSelectedMateriales(ids) {
    const idSet = new Set(asArray(ids).map(String));
    const prevById = new Map(asArray(dialog.form.rows).map((r) => [String(r.materialId), r]));

    const nextRows = [];
    for (const materialId of idSet) {
      const prev = prevById.get(materialId);
      nextRows.push(
        prev ?? {
          materialId,
          cantidad: 1,
        }
      );
    }

    dialog.setField("rows", nextRows);
  }

  function patchCantidad(materialId, cantidad) {
    dialog.setField(
      "rows",
      asArray(dialog.form.rows).map((r) => (String(r.materialId) === String(materialId) ? { ...r, cantidad } : r))
    );
  }

  function removeRow(materialId) {
    dialog.setField(
      "rows",
      asArray(dialog.form.rows).filter((r) => String(r.materialId) !== String(materialId))
    );
  }

  async function save() {
    setLocalError(null);

    const needsMaterials = dialog.form.materialesRequeridos !== false;
    if (!needsMaterials) {
      try {
        for (const row of dbRows) {
          await deleteMaterial.mutateAsync({ actividadId, materialId: row.materialId });
        }

        await patch.mutateAsync({
          actividadId,
          patch: { materialesRequeridos: false },
        });

        dialog.close();
        return;
      } catch (e) {
        setLocalError(String(e?.message || e));
        throw e;
      }
    }

    const prevById = new Map(dbRows.map((r) => [String(r.materialId), r]));
    const nextRows = asArray(dialog.form.rows).map((r) => ({
      materialId: String(r.materialId),
      cantidad: toIntOrNull(r.cantidad),
    }));

    const invalidRow = nextRows.find((r) => !r.materialId || r.cantidad == null || r.cantidad < 1);
    if (invalidRow) {
      setLocalError("Cada material debe tener una cantidad entera mayor o igual a 1.");
      return;
    }

    const nextIds = new Set(nextRows.map((r) => r.materialId));
    const toDelete = dbRows.filter((r) => !nextIds.has(String(r.materialId)));
    const toUpsert = nextRows.filter((r) => {
      const prev = prevById.get(r.materialId);
      return !prev || Number(prev.cantidad) !== Number(r.cantidad);
    });

    try {
      for (const row of toDelete) {
        await deleteMaterial.mutateAsync({ actividadId, materialId: row.materialId });
      }

      for (const row of toUpsert) {
        await upsertMaterial.mutateAsync({
          actividadId,
          materialId: row.materialId,
          cantidad: row.cantidad,
        });
      }

      if (!materialesRequeridos) {
        await patch.mutateAsync({
          actividadId,
          patch: { materialesRequeridos: true },
        });
      }

      dialog.close();
    } catch (e) {
      setLocalError(String(e?.message || e));
      throw e;
    }
  }

  return (
    <>
      <DataCard
        title="Materiales"
        variant="orange"
        headerIcon={<FaBox size="24px" />}
        onEdit={openDialog}
        editAriaLabel="Editar materiales"
        isIncomplete={materialesRequeridos && !materialesQ.isLoading && dbRows.length === 0}
      >
        {materialesQ.isLoading ? (
          <Text color="dashboard.fg.muted" fontSize="sm">Cargando materiales...</Text>
        ) : !materialesRequeridos ? (
          <Text fontWeight="700" color="dashboard.fg.default" fontSize="sm">No se requieren materiales.</Text>
        ) : dbRows.length === 0 ? (
          <Text color="dashboard.fg.muted" fontSize="sm">Sin materiales agregados.</Text>
        ) : (
          <Grid templateColumns={{ base: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }} gap="2.5" w="full" minW="0">
            {dbRows.map((row) => {
              const material = materialById.get(String(row.materialId));
              const tone = materialTone(row.materialId || material?.nombre || "material");
              return (
                <Box key={row.materialId} rounded="2xl" p="3" bg={tone.bg} borderWidth="1px" borderColor="dashboard.border.muted" minW="0" maxW="full" overflow="hidden">
                  <HStack justify="space-between" gap="3" minW="0" maxW="full">
                    <HStack gap="2.5" minW="0" flex="1" overflow="hidden">
                      <Box boxSize="9" rounded="pill" bg={tone.solid} display="grid" placeItems="center" color="white" fontWeight="900" flexShrink={0}>
                        <FaBox size="16px" />
                      </Box>
                      <DashboardTruncatedText fontWeight="800" fontSize="sm" color="dashboard.fg.default">
                        {material?.nombre || "Material"}
                      </DashboardTruncatedText>
                    </HStack>
                    <Box rounded="pill" px="3" py="1" bg={tone.solid} color="white" fontSize="sm" fontWeight="900" lineHeight="1" flexShrink={0} maxW="45%" overflow="hidden" textOverflow="ellipsis">
                      {row.cantidad}
                    </Box>
                  </HStack>
                </Box>
              );
            })}
          </Grid>
        )}
      </DataCard>

      <FormDialog
        open={dialog.open}
        onOpenChange={dialog.onOpenChange}
        title="Editar Materiales"
        errorText={errorText}
        saving={saving}
        onCancel={dialog.close}
        onSave={save}
      >
        <HStack justify="space-between" align="center">
          <Box>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default">Materiales</Text>
            <Text color="dashboard.fg.muted" fontSize="sm">¿Se necesitan materiales para esta actividad?</Text>
          </Box>
          <Switch.Root size="lg" checked={dialog.form.materialesRequeridos !== false} onCheckedChange={(e) => setMaterialesRequeridos(!!e.checked)}>
            <Switch.HiddenInput />
            <Switch.Control>
              <Switch.Thumb>
                <Switch.ThumbIndicator fallback={<HiX color="black" />}>
                  <HiCheck />
                </Switch.ThumbIndicator>
              </Switch.Thumb>
            </Switch.Control>
          </Switch.Root>
        </HStack>

        {dialog.form.materialesRequeridos !== false && (
          <TagCombobox
            label="Materiales solicitados"
            placeholder="Buscar material..."
            items={materialItems}
            value={asArray(dialog.form.rows).map((r) => String(r.materialId))}
            onChange={setSelectedMateriales}
            allowCustomValue={false}
          />
        )}

        {dialog.form.materialesRequeridos !== false && asArray(dialog.form.rows).length > 0 ? (
          <Stack gap="2">
            <Grid templateColumns="2fr 1fr auto" gap="3" px="2" py="2" bg="dashboard.bg.surfaceMuted" rounded="md">
              <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">Material</Text>
              <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">Cantidad</Text>
              <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted"></Text>
            </Grid>

            {asArray(dialog.form.rows).map((row) => {
              const material = materialById.get(String(row.materialId));
              return (
                <Grid key={row.materialId} templateColumns="2fr 1fr auto" gap="3" px="2" py="2" alignItems="center">
                  <Text>{material?.nombre || "Material"}</Text>
                  <DashboardInput
                    type="number"
                    min={1}
                    step={1}
                    value={String(row.cantidad ?? "")}
                    onChange={(e) => patchCantidad(row.materialId, e.target.value)}
                  />
                  <DashboardDeleteButton aria-label="Eliminar material" onClick={() => removeRow(row.materialId)} />
                </Grid>
              );
            })}
          </Stack>
        ) : null}
      </FormDialog>
    </>
  );
};
